import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Mail, Phone, Calendar, Search, Eye, Trash2, Check, X, MessageSquare,
  ChevronLeft, ChevronRight
} from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';

const EnquiriesManagement = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchEnquiries();
  }, [currentPage]);

  const fetchEnquiries = async () => {
    setLoading(true);
    try {
      const skip = (currentPage - 1) * itemsPerPage;
      const response = await api.get(`/admin/enquiries?skip=${skip}&limit=${itemsPerPage}`);
      setEnquiries(response.data.enquiries || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error('Failed to fetch enquiries');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (enquiryId, status) => {
    try {
      const formData = new FormData();
      formData.append('status', status);
      await api.put(`/admin/enquiries/${enquiryId}/status`, formData);
      toast.success('Status updated');
      fetchEnquiries();
      if (selectedEnquiry?.id === enquiryId) {
        setSelectedEnquiry(prev => ({ ...prev, status }));
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const deleteEnquiry = async (enquiryId) => {
    if (!confirm('Are you sure you want to delete this enquiry?')) return;
    
    try {
      await api.delete(`/admin/enquiries/${enquiryId}`);
      toast.success('Enquiry deleted');
      fetchEnquiries();
      if (selectedEnquiry?.id === enquiryId) {
        setSelectedEnquiry(null);
      }
    } catch (error) {
      toast.error('Failed to delete enquiry');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-teal-100 text-teal-700';
      case 'read': return 'bg-amber-100 text-amber-700';
      case 'responded': return 'bg-green-100 text-green-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const filteredEnquiries = enquiries.filter(e => 
    e.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.message?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(total / itemsPerPage);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{fontFamily: 'Outfit'}}>Contact Enquiries</h1>
          <p className="text-slate-600 mt-1">Manage enquiries from the contact form</p>
        </div>
        <div className="text-sm text-slate-600">
          Total: <span className="font-semibold">{total}</span> enquiries
        </div>
      </div>

      {/* Search */}
      <Card className="p-4 mb-6 border-slate-200">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Search by name, email, or message..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 h-11"
          />
        </div>
      </Card>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Enquiries List */}
        <div className="lg:col-span-2">
          <Card className="border-slate-200 overflow-hidden">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto"></div>
              </div>
            ) : filteredEnquiries.length === 0 ? (
              <div className="text-center py-12">
                <Mail className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-600">No enquiries found</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-200">
                {filteredEnquiries.map((enquiry) => (
                  <div 
                    key={enquiry.id}
                    className={`p-4 hover:bg-slate-50 cursor-pointer transition-colors ${selectedEnquiry?.id === enquiry.id ? 'bg-teal-50' : ''}`}
                    onClick={() => {
                      setSelectedEnquiry(enquiry);
                      if (enquiry.status === 'new') {
                        updateStatus(enquiry.id, 'read');
                      }
                    }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-slate-900">{enquiry.name}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(enquiry.status)}`}>
                            {enquiry.status}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600">{enquiry.email}</p>
                        <p className="text-sm text-slate-500 mt-1 line-clamp-2">{enquiry.message}</p>
                        <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(enquiry.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="p-4 border-t border-slate-200 flex items-center justify-between">
                <p className="text-sm text-slate-600">
                  Page {currentPage} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(p => p - 1)}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(p => p + 1)}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </div>

        {/* Enquiry Detail */}
        <div>
          <Card className="border-slate-200 p-6 sticky top-6">
            {selectedEnquiry ? (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-slate-900">Enquiry Details</h3>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setSelectedEnquiry(null)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-sm text-slate-500">Name</label>
                    <p className="font-medium text-slate-900">{selectedEnquiry.name}</p>
                  </div>
                  
                  <div>
                    <label className="text-sm text-slate-500">Email</label>
                    <p className="font-medium text-slate-900 flex items-center gap-2">
                      <Mail className="w-4 h-4 text-slate-400" />
                      <a href={`mailto:${selectedEnquiry.email}`} className="text-teal-600 hover:underline">
                        {selectedEnquiry.email}
                      </a>
                    </p>
                  </div>

                  {selectedEnquiry.phone && (
                    <div>
                      <label className="text-sm text-slate-500">Phone</label>
                      <p className="font-medium text-slate-900 flex items-center gap-2">
                        <Phone className="w-4 h-4 text-slate-400" />
                        <a href={`tel:${selectedEnquiry.phone}`} className="text-teal-600 hover:underline">
                          {selectedEnquiry.phone}
                        </a>
                      </p>
                    </div>
                  )}

                  <div>
                    <label className="text-sm text-slate-500">Message</label>
                    <p className="text-slate-700 bg-slate-50 p-3 rounded-lg mt-1">
                      {selectedEnquiry.message}
                    </p>
                  </div>

                  <div>
                    <label className="text-sm text-slate-500">Received</label>
                    <p className="text-slate-700">{formatDate(selectedEnquiry.created_at)}</p>
                  </div>

                  <div>
                    <label className="text-sm text-slate-500 mb-2 block">Status</label>
                    <div className="flex gap-2">
                      <Button 
                        variant={selectedEnquiry.status === 'new' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => updateStatus(selectedEnquiry.id, 'new')}
                        className={selectedEnquiry.status === 'new' ? 'bg-teal-600' : ''}
                      >
                        New
                      </Button>
                      <Button 
                        variant={selectedEnquiry.status === 'read' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => updateStatus(selectedEnquiry.id, 'read')}
                        className={selectedEnquiry.status === 'read' ? 'bg-amber-600' : ''}
                      >
                        Read
                      </Button>
                      <Button 
                        variant={selectedEnquiry.status === 'responded' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => updateStatus(selectedEnquiry.id, 'responded')}
                        className={selectedEnquiry.status === 'responded' ? 'bg-green-600' : ''}
                      >
                        Responded
                      </Button>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-slate-200">
                    <Button 
                      variant="destructive" 
                      className="w-full"
                      onClick={() => deleteEnquiry(selectedEnquiry.id)}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Enquiry
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <MessageSquare className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p className="text-slate-500">Select an enquiry to view details</p>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default EnquiriesManagement;
